import { useState } from 'react';
import { Head, Link } from '@inertiajs/react';

/**
 * Comic image data from the backend.
 */
interface ComicImage {
    id: number;
    url: string;
    alt_text: string | null;
    sort_order: number;
}

/**
 * Comic data from the backend.
 */
interface Comic {
    id: number;
    title: string | null;
    slug: string;
    artist_name: string;
    artist_url: string | null;
    is_published: boolean;
    published_at: string | null;
    images: ComicImage[];
}

/**
 * Navigation comic data.
 */
interface NavComic {
    title: string | null;
    slug: string;
    artist_name: string;
}

/**
 * Props for the Show page.
 */
interface Props {
    comic: Comic;
    prevComic: NavComic | null;
    nextComic: NavComic | null;
    isGm: boolean;
}

/**
 * Public Comic Show page - displays a single comic with image navigation.
 * Uses traditional newspaper styling with white background and black text.
 */
export default function Show({ comic, prevComic, nextComic, isGm }: Props) {
    // Current image index for multi-panel comics
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const displayTitle = comic.title || `Feature by ${comic.artist_name}`;
    const currentImage = comic.images[currentImageIndex];
    const hasMultipleImages = comic.images.length > 1;

    // Navigate to previous image
    const handlePrevImage = () => {
        setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : comic.images.length - 1));
    };

    // Navigate to next image
    const handleNextImage = () => {
        setCurrentImageIndex((prev) => (prev < comic.images.length - 1 ? prev + 1 : 0));
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
            handlePrevImage();
        } else if (e.key === 'ArrowRight') {
            handleNextImage();
        }
    };

    return (
        <>
            <Head title={`${displayTitle} - Features - The Feywild Times`} />

            <div className="min-h-screen bg-gray-50">
                {/* Newspaper Header */}
                <header className="bg-white border-b-4 border-double border-gray-900 py-4">
                    <div className="max-w-6xl mx-auto px-4 text-center">
                        <Link href="/newspaper/comics" className="text-gray-600 hover:text-gray-500 text-sm mb-2 inline-block">
                            ← Back to Features
                        </Link>
                        <h1 className="text-2xl md:text-3xl font-serif font-bold text-black tracking-tight">
                            {displayTitle}
                        </h1>
                        <p className="text-gray-600 font-serif mt-1">
                            by{' '}
                            {comic.artist_url ? (
                                <a
                                    href={comic.artist_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-gray-500"
                                >
                                    {comic.artist_name}
                                </a>
                            ) : (
                                comic.artist_name
                            )}
                        </p>
                        {isGm && !comic.is_published && (
                            <span className="inline-block mt-2 px-2 py-0.5 bg-yellow-500 text-white rounded text-xs">
                                Draft
                            </span>
                        )}
                    </div>
                </header>

                {/* Main Content */}
                <main className="max-w-4xl mx-auto px-4 py-6">
                    {/* Comic Image Viewer */}
                    <div
                        className="bg-white border border-gray-300 rounded-lg overflow-hidden"
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                    >
                        {/* Image */}
                        <div className="relative">
                            {currentImage && (
                                <img
                                    src={currentImage.url}
                                    alt={currentImage.alt_text || `${displayTitle} - Panel ${currentImageIndex + 1}`}
                                    className="w-full h-auto"
                                />
                            )}

                            {/* Image Navigation Arrows (for multi-panel comics) */}
                            {hasMultipleImages && (
                                <>
                                    <button
                                        onClick={handlePrevImage}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                                        aria-label="Previous panel"
                                    >
                                        ←
                                    </button>
                                    <button
                                        onClick={handleNextImage}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                                        aria-label="Next panel"
                                    >
                                        →
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Image Caption (alt text) */}
                        {currentImage?.alt_text && (
                            <div className="px-4 py-3 border-t border-gray-200 text-center text-sm text-gray-600 italic font-serif">
                                {currentImage.alt_text}
                            </div>
                        )}

                        {/* Image Counter (for multi-panel comics) */}
                        {hasMultipleImages && (
                            <div className="px-4 py-2 border-t border-gray-200 text-center text-sm text-gray-600">
                                Panel {currentImageIndex + 1} of {comic.images.length}
                                <span className="text-xs text-gray-400 ml-2">(Use ← → keys to navigate)</span>
                            </div>
                        )}
                    </div>

                    {/* Prev/Next Comic Navigation */}
                    <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-300">
                        {prevComic ? (
                            <Link
                                href={`/newspaper/comics/${prevComic.slug}`}
                                className="text-gray-700 hover:text-gray-500 transition-colors"
                            >
                                <span className="text-sm">← Previous</span>
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    {prevComic.title || `by ${prevComic.artist_name}`}
                                </span>
                            </Link>
                        ) : (
                            <div />
                        )}

                        <Link
                            href="/newspaper/comics"
                            className="text-gray-600 hover:text-gray-500 text-sm underline"
                        >
                            All Features
                        </Link>

                        {nextComic ? (
                            <Link
                                href={`/newspaper/comics/${nextComic.slug}`}
                                className="text-gray-700 hover:text-gray-500 transition-colors text-right"
                            >
                                <span className="text-sm">Next →</span>
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    {nextComic.title || `by ${nextComic.artist_name}`}
                                </span>
                            </Link>
                        ) : (
                            <div />
                        )}
                    </div>
                </main>
            </div>
        </>
    );
}

